import { PurchaseItem } from "@/models/purchase-item.model"
import { SaleItem } from "@/models/sale-item.model"
import { RentalIntakeItem } from "@/models/rental-intake-item.model"
import { RentalIntake } from "@/models/rental-intake.model"
import { type IAdministrativeEventAndTypeCombination } from "@/interfaces/component/administrative-event-and-type-combination.interface"
import { AdministrativeEventTypes } from "@/enums/administrative-event-types.enum"
import { unitToAlternativeName } from "@/helper/enums/unit-helper.ts"

export class InstructionProcessingRowComboboxHelper {
  public static buildComboboxItemsList(
    purchaseItems: PurchaseItem[],
    saleItems: SaleItem[],
    rentalIntakeItems: RentalIntakeItem[],
    returnableRentalIntakes: RentalIntake[]
  ) {
    const _combinedItems: IAdministrativeEventAndTypeCombination[] = []

    _combinedItems.push(...this.buildPurchaseComboboxItemsList(purchaseItems))
    for (const saleItem of saleItems) {
      _combinedItems.push({
        key: `${AdministrativeEventTypes.SALE}-${saleItem.id}`,
        type: AdministrativeEventTypes.SALE,
        administrativeEventItem: saleItem,
        getSearchText: () => this.getSaleItemSearchText(saleItem)
      })
    }
    for (const rentalIntakeItem of rentalIntakeItems) {
      if (rentalIntakeItem.rentalDispenseItem) {
        _combinedItems.push(
          ...[
            {
              key: `${AdministrativeEventTypes.RENTAL_INTAKE_AND_DISPENSE_ISSUANCE}-${rentalIntakeItem.id}-${rentalIntakeItem.rentalDispenseItem.id}`,
              type: AdministrativeEventTypes.RENTAL_INTAKE_AND_DISPENSE_ISSUANCE,
              administrativeEventItem: rentalIntakeItem,
              getSearchText: () => this.getRentalMutationIssuanceGroupSearchText(rentalIntakeItem)
            }
          ]
        )
      }
    }
    for (const rentalIntake of returnableRentalIntakes) {
      if (rentalIntake.associatedRentalDispense) {
        _combinedItems.push({
          key: `${AdministrativeEventTypes.RENTAL_INTAKE_AND_DISPENSE_RETURN}-${rentalIntake.id}-${rentalIntake.associatedRentalDispense.id}`,
          type: AdministrativeEventTypes.RENTAL_INTAKE_AND_DISPENSE_RETURN,
          administrativeEventItem: rentalIntake,
          getSearchText: () => this.getRentalMutationReturnGroupSearchText(rentalIntake)
        })
      }
    }
    return _combinedItems
  }

  public static buildPurchaseComboboxItemsList(purchaseItems: PurchaseItem[]) {
    const items: IAdministrativeEventAndTypeCombination<AdministrativeEventTypes.PURCHASE>[] = []

    for (const purchaseItem of purchaseItems) {
      items.push({
        key: `${AdministrativeEventTypes.PURCHASE}-${purchaseItem.id}`,
        type: AdministrativeEventTypes.PURCHASE,
        administrativeEventItem: purchaseItem,
        getSearchText: () => this.getPurchaseItemSearchText(purchaseItem)
      })
      if (purchaseItem.saleItem) {
        items.push({
          key: `${AdministrativeEventTypes.PURCHASE_AND_SALE}-${purchaseItem.id}-${purchaseItem.saleItem.id}`,
          type: AdministrativeEventTypes.PURCHASE_AND_SALE,
          administrativeEventItem: purchaseItem,
          getSearchText: () => this.getTransactionGroupSearchText(purchaseItem)
        })
      }
    }
    return items
  }

  public static getPurchaseItemSearchText(purchaseItem: PurchaseItem) {
    return `Inkoop ${purchaseItem.getDisplayText()} ${unitToAlternativeName(purchaseItem.unit)}`
  }

  public static getSaleItemSearchText(saleItem: SaleItem) {
    return `Verkoop ${saleItem.getDisplayText()} ${unitToAlternativeName(saleItem.unit)}`
  }

  public static getTransactionGroupSearchText(
    purchaseItem: PurchaseItem,
    saleItem = purchaseItem.saleItem
  ) {
    return `Inkoop en verkoop ${purchaseItem.getDisplayText()} ${unitToAlternativeName(purchaseItem.unit)} ${saleItem.getDisplayText()}`
  }

  private static getRentalMutationIssuanceGroupSearchText(rentalIntakeItem: RentalIntakeItem) {
    return `Inhuur en verhuur uitgifte ${rentalIntakeItem.getDisplayText()} ${unitToAlternativeName(rentalIntakeItem.unit)} ${rentalIntakeItem.rentalDispenseItem.getDisplayText()}`
  }

  public static getRentalMutationReturnGroupSearchText(rentalIntake: RentalIntake) {
    return `Inhuur en verhuur retour ${rentalIntake.rentalIntakeItem.getDisplayText()} ${unitToAlternativeName(rentalIntake.rentalIntakeItem.unit)} ${rentalIntake.associatedRentalDispense!.rentalDispenseItem.getDisplayText()} ${rentalIntake.project?.getDisplayText()}`
  }
}
